/**
 * Adressen til Spillesteder-siden: fanen, visningen og filtrene ligger i URL-en, som på Booking —
 * «se på salene i Bodø over 600 med nightliner» skal kunne sendes til en kollega, og overleve en
 * omlasting. En ukjent verdi faller stille bort: et gammelt bokmerke er ikke en feil.
 */
import type { VenueListingView } from '@tvitvi/shared';

import type { VenueListingFilters } from './venue-market-queries';
import { currentMonth, isDateKey, isMonthKey } from './venue-month';

export const VENUES_TABS = ['all', 'favorites', 'used', 'requests'] as const;
export type VenuesTab = (typeof VENUES_TABS)[number];

export const VENUES_DISPLAYS = ['cards', 'list', 'map'] as const;
export type VenuesDisplay = (typeof VENUES_DISPLAYS)[number];

export interface VenuesSearch {
  tab?: VenuesTab;
  display?: VenuesDisplay;
  q?: string;
  /** Fylker, kommaliste. */
  counties?: string;
  /** «Fylke|Kommune», kommaliste. */
  places?: string;
  min?: number;
  max?: number;
  piano?: boolean;
  nightliner?: boolean;
  wheelchair?: boolean;
  month?: string;
  freeOn?: string;
  freeMonth?: boolean;
  /** En sal i plattformregisteret som står åpen («Om stedet»). */
  open?: string;
  /** Funn 27: en egen forespørsel som står åpen fra «Mine forespørsler». */
  request?: string;
}

function text(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed.slice(0, 200);
}

function count(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 1_000_000 ? parsed : undefined;
}

function flag(value: unknown): boolean | undefined {
  return value === true || value === 'true' || value === '1' ? true : undefined;
}

function uuid(value: unknown): string | undefined {
  return typeof value === 'string' && /^[0-9a-f-]{36}$/u.test(value) ? value : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return allowed.find((one) => one === value);
}

export function validateVenuesSearch(search: Record<string, unknown>): VenuesSearch {
  const out: VenuesSearch = {};
  const tab = oneOf(search['tab'], VENUES_TABS);
  if (tab !== undefined && tab !== 'all') out.tab = tab;
  const display = oneOf(search['display'], VENUES_DISPLAYS);
  if (display !== undefined && display !== 'cards') out.display = display;
  const q = text(search['q']);
  if (q !== undefined) out.q = q;
  const counties = text(search['counties']);
  if (counties !== undefined) out.counties = counties;
  const places = text(search['places']);
  if (places !== undefined) out.places = places;
  const min = count(search['min']);
  if (min !== undefined) out.min = min;
  const max = count(search['max']);
  if (max !== undefined) out.max = max;
  if (flag(search['piano']) === true) out.piano = true;
  if (flag(search['nightliner']) === true) out.nightliner = true;
  if (flag(search['wheelchair']) === true) out.wheelchair = true;
  if (isMonthKey(search['month'])) out.month = search['month'];
  if (isDateKey(search['freeOn'])) out.freeOn = search['freeOn'];
  if (flag(search['freeMonth']) === true) out.freeMonth = true;
  const open = uuid(search['open']);
  if (open !== undefined) out.open = open;
  const request = uuid(search['request']);
  if (request !== undefined) out.request = request;
  return out;
}

function list(value: string | undefined): string[] {
  return value === undefined
    ? []
    : value
        .split(',')
        .map((one) => one.trim())
        .filter((one) => one !== '');
}

export function tabOf(search: VenuesSearch): VenuesTab {
  return search.tab ?? 'all';
}

export function displayOf(search: VenuesSearch): VenuesDisplay {
  return search.display ?? 'cards';
}

/** Adressen som filtrene lista sender. «Mine forespørsler» har ingen liste å filtrere. */
export function filtersOf(search: VenuesSearch, today: Date = new Date()): VenueListingFilters {
  const tab = tabOf(search);
  const view: VenueListingView = tab === 'favorites' || tab === 'used' ? tab : 'all';
  return {
    view,
    search: search.q ?? '',
    counties: list(search.counties),
    municipalities: list(search.places),
    minCapacity: search.min ?? null,
    maxCapacity: search.max ?? null,
    grandPiano: search.piano === true,
    nightliner: search.nightliner === true,
    wheelchair: search.wheelchair === true,
    month: search.month ?? currentMonth(today),
    freeOn: search.freeOn ?? null,
    freeInMonth: search.freeMonth === true,
  };
}

/** Er noe filtrert bort? «Nullstill» vises bare da. */
export function hasFilters(search: VenuesSearch): boolean {
  return (
    search.q !== undefined ||
    search.counties !== undefined ||
    search.places !== undefined ||
    search.min !== undefined ||
    search.max !== undefined ||
    search.piano === true ||
    search.nightliner === true ||
    search.wheelchair === true ||
    search.freeOn !== undefined ||
    search.freeMonth === true
  );
}

/** Legg til eller ta bort én verdi i en kommaliste. */
export function toggleInList(value: string | undefined, item: string): string | undefined {
  const items = list(value);
  const next = items.includes(item) ? items.filter((one) => one !== item) : [...items, item];
  return next.length === 0 ? undefined : next.join(',');
}

export function listOf(value: string | undefined): string[] {
  return list(value);
}

/** Salens side utenfra: fanen og måneden kalenderen står på. */
export const ROOM_TABS = ['overview', 'technical', 'calendar', 'arrival', 'documents'] as const;
export type RoomTab = (typeof ROOM_TABS)[number];

export interface VenueRoomSearch {
  tab?: RoomTab;
  month?: string;
}

export function validateVenueRoomSearch(search: Record<string, unknown>): VenueRoomSearch {
  const out: VenueRoomSearch = {};
  const tab = ROOM_TABS.find((one) => one === search['tab']);
  if (tab !== undefined && tab !== 'overview') out.tab = tab;
  if (isMonthKey(search['month'])) out.month = search['month'];
  return out;
}
